<template>
    <div class="page-layout">
        <div class="page-toolbar">
            <div class="toolbar-bulk-edit">
                <md-button
                    class="md-primary md-raised bulk-edit-button"
                    @click="showBulkActionsWizard"
                    :disabled="selectedRows.length === 0"
                >
                    {{ `Bulk Change ${selectedRows.length} Records` }}
                </md-button>
            </div>
            <div class="toolbar-text">
                <input
                    type="text"
                    class="search-textbox"
                    placeholder="Just start typing to search all fields..."
                    v-model="searchText"
                    @keyup="searchTextChanged"
                    autofocus
                />
            </div>
            <div class="toolbar-special date-chip-selector">
                <transition name="fade-scale" mode="out-in">
                    <!-- Calendar icon when no special date selected -->
                    <md-button
                        v-if="!selectedSpecialDate"
                        class="md-icon-button"
                        @click="toggleSpecialDateFilter"
                        key="calendar"
                    >
                        <md-icon class="when-icon">edit_calendar</md-icon>
                    </md-button>

                    <!-- When a special date is selected -->
                    <md-chip v-else md-deletable @md-delete="clearSpecialDate" class="chip">
                        <md-icon class="when-icon selected">event_available</md-icon>
                        <span class="when-text">{{ selectedSpecialDateLabel }}</span>
                    </md-chip>
                </transition>

                <!-- The special dates filter allows selection of a magic variable that will affect the 'When' field. This is the popup selection. -->
                <div v-if="specialDateFilterOpen" class="special-date-filter" v-click-outside="exitSpecialDatesFilter">
                    <md-card class="special-date-filter-card">
                        <md-card-header>Filters results against the 'When' column</md-card-header>

                        <md-list>
                            <md-list-item @click="selectSpecialDate(3)" class="list-item">
                                <div class="when-list-item">
                                    <md-icon class="when-icon-option col1">date_range</md-icon>
                                    <span class="when-text col2">Yesterday</span>
                                </div>
                            </md-list-item>

                            <md-list-item @click="selectSpecialDate(1)" class="list-item">
                                <div class="when-list-item">
                                    <md-icon class="when-icon-option col1">today</md-icon>
                                    <span class="when-text col2">Today</span>
                                </div>
                            </md-list-item>

                            <md-list-item @click="selectSpecialDate(2)" class="list-item">
                                <div class="when-list-item">
                                    <md-icon class="when-icon-option col1">event</md-icon>
                                    <span class="when-text col2">Tomorrow</span>
                                </div>
                            </md-list-item>
                        </md-list>
                    </md-card>
                </div>
            </div>
            <div class="toolbar-filters">
                <filter-manager
                    :filter-modified.sync="filterModified"
                    :filter-object="gridSearchState"
                    @selectedFilterChanged="selectedFilterChanged"
                ></filter-manager>
            </div>
        </div>
        <div class="page-content">
            <ag-grid-vue
                class="grid-display"
                row-model-type="serverSide"
                :class="gridThemeClass"
                :column-defs="columnDefs"
                :grid-options="gridOptions"
                :default-col-def="defaultColDef"
                :pagination="true"
                :pagination-page-size="gridPageSize"
                :pagination-page-size-selector="[10, 15, 20, 50, 100]"
                :suppress-excel-export="false"
                :auto-group-column-def="gridAutoGroupColumnDef"
                :row-selection="rowSelection"
                :side-bar="gridSideBar"
                :pivot-panel-show="gridPivotPanelShow"
                :group-selects-children="true"
                :overlay-loading-template="gridLoadingOverlayTemplate"
                @grid-ready="onGridReady"
                @filter-changed="onGridFilterChanged"
                @sort-changed="onGridSortChanged"
                @column-resized="onColumnResized"
                :pagination-number-formatter="paginationNumberFormatter"
                :tooltip-interaction="true"
            ></ag-grid-vue>
        </div>

        <!-- Bulk Actions Wizard Modal -->
        <bulk-actions-wizard
            v-if="showWizard"
            :selected-rows="selectedRows"
            @close="closeWizard"
            @refresh="refreshAfterBulkAction"
        />
    </div>
</template>

<script>
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridVue } from 'ag-grid-vue';
import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import { GeneralMixin } from '@/mixins';
import { handleRequests } from '@/helpers';
import { STATUS_CONSTANTS } from '@/utils/constants';
import StopStatusRenderer from './grid-components/StopStatusRenderer';
import GridChipsRenderer from './grid-components/GridChipsRenderer';
import GridDropDownFilter from './grid-components/GridDropDownFilter';
import TeamRegionDropdownFilter from './grid-components/TeamRegionDropdownFilter';
import TeamRegionFilterCellRenderer from './grid-components/TeamRegionFilterCellRenderer';
import GridLinkRenderer from './grid-components/GridLinkRenderer';
import GridItemDataTooltip from './grid-components/GridItemDataTooltip';
import GridKeyValueDataTooltip from './grid-components/GridKeyValueDataTooltip';
import AssignDriverCellRenderer from './grid-components/AssignDriverCellRenderer';
import FilterManager from './components/FilterManager';
import BulkActionsWizard from './components/BulkActionsWizard';

// Much of this code is oriented around the AG-GRID component.
// https://www.ag-grid.com/vue-data-grid/vue2/

export default {
    name: 'JobsFilter',
    components: {
        AgGridVue,
        // eslint-disable-next-line vue/no-unused-components
        GridDropDownFilter,
        // eslint-disable-next-line vue/no-unused-components
        TeamRegionDropdownFilter,
        // eslint-disable-next-line vue/no-unused-components
        TeamRegionFilterCellRenderer,
        // eslint-disable-next-line vue/no-unused-components
        StopStatusRenderer,
        // eslint-disable-next-line vue/no-unused-components
        GridLinkRenderer,
        // eslint-disable-next-line vue/no-unused-components
        GridItemDataTooltip,
        // eslint-disable-next-line vue/no-unused-components
        BulkActionsWizard,
        FilterManager,
        // eslint-disable-next-line vue/no-unused-components
        GridKeyValueDataTooltip,
        // eslint-disable-next-line vue/no-unused-components
        GridChipsRenderer,
        // eslint-disable-next-line vue/no-unused-components
        AssignDriverCellRenderer
    },
    mixins: [GeneralMixin],
    async beforeMount() {
        // There is an issue where we need to have the column definitions defined before we get dynamic data to show in filter drop downs.
        // This sets up the columns first with no dynamic data, and they are updated later when the data is fetched.
        this.setupColumns(false);
    },
    async mounted() {
        await this.FETCH_TEAM_CUSTOMERS();
        await this.FETCH_SKILLS();
        await this.FETCH_TEAM_REGIONS();
        await this.getTeamMembers();

        this.setupCustomFieldDefinitions();

        const coldefs = this.setupColumns(true);
        if (this.gridApi) {
            // We need to set them this way because they were already set previously in beforeMount.
            this.gridApi.setGridOption('columnDefs', coldefs);
            this.setupColumnToolbox();
        }

        this.isMounted = true;

        if (this.gridApi && this.currentFilter) {
            // Its possible that either the grid mounts first, or this component mounts first.
            // We never know which (non-deterministic) so we need to check if the grid is ready and then load the filter.
            // This same check happens in onGridReady.
            this.defaultColumnState = this.gridApi.getColumnState();
            this.loadFilter(this.currentFilter);
        }
    },
    data() {
        return {
            isMounted: false,
            teamMembers: [],
            customFieldDefinitions: [],
            filterModified: false,
            gridSearchState: null, // Comprised of: filterModel, sortModel, and column State
            currentFilter: null,
            columnDefs: null, // We set the column definitions later because of the need to fill values in custom filters
            defaultColDef: {
                // All columns will get these properties unless otherwise specified (overridden on the column definitions)
                enableRowGroup: false,
                enablePivot: false,
                enableValue: false,
                editable: false,
                floatingFilter: true,
                filter: 'agTextColumnFilter',
                filterParams: {
                    maxNumConditions: 1
                }
            },
            defaultColumnState: null,
            rowSelection: 'multiple',
            gridThemeClass: 'ag-theme-quartz',
            gridPageSize: 100,
            gridSideBar: {
                // Configures the pop out side bar on the right where user's can select additional columns to show
                toolPanels: [
                    {
                        id: 'columns',
                        labelDefault: 'Columns',
                        labelKey: 'columns',
                        iconKey: 'columns',
                        toolPanel: 'agColumnsToolPanel',
                        toolPanelParams: {
                            suppressRowGroups: true,
                            suppressValues: true,
                            suppressPivots: true,
                            suppressPivotMode: true,
                            suppressSideButtons: false,
                            suppressColumnFilter: false,
                            suppressColumnSelectAll: false,
                            suppressColumnExpandAll: false,
                            suppressSyncLayoutWithGrid: true,
                            suppressColumnMove: true
                        }
                    }
                ],
                allowDragFromColumnsToolPanel: true,
                suppressRowGroups: true,
                suppressMovable: true,
                suppressValues: true,
                suppressPivots: true
            },
            gridOptions: {
                getRowId: (params) => params.data.stopId,
                rowSelection: 'multiple',
                suppressRowClickSelection: true,
                onSelectionChanged: (event) => {
                    this.onSelectionChanged(event);
                }
            },
            gridRowGroupPanelShow: 'always',
            gridPivotPanelShow: 'never',
            gridAutoGroupColumnDef: {
                cellRenderer: 'agGroupCellRenderer',
                cellRendererParams: {
                    checkbox: true
                }
            },
            paginationNumberFormatter: (params) => {
                if (params.value >= 10000) 
                    return 'Over 10,000';
                return params.value;
            },
            gridApi: null,
            stopsSearchResults: [],
            totalStops: 0,
            isGridLoading: false,
            gridLoadingOverlayTemplate:
                '<div aria-live="polite" aria-atomic="true" style="position:absolute;top:0;left:0;right:0; bottom:0; background: url(https://ag-grid.com/images/ag-grid-loading-spinner.svg) center no-repeat" aria-label="loading"></div>',
            searchText: '',

            // special dates state
            selectedSpecialDate: null,
            specialDateFilterOpen: false, // Controls the menu state

            // bulk actions state
            selectedRows: [],
            showWizard: false
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user',
            customerList: 'team/customers',
            skills: 'team/skills',
            teamRegions: 'team/teamRegions',
            isSupportUser: 'user/isSupportUser'
        }),
        selectedSpecialDateLabel() {
            switch (this.selectedSpecialDate) {
                case 1:
                    return 'Today';
                case 2:
                    return 'Tomorrow';
                case 3:
                    return 'Yesterday';
                default:
                    return '';
            }
        },
        teamRegionOptions() {
            if (!this.teamRegions) 
                return [];

            const isTeamManager = this.user?.role === 'Team Manager';
            const trs = this.teamRegions.map((x) => ({
                level: x.level,
                name: x.name,
                teamRegionId: x.teamRegionId,
                parentTeamRegionId: x.parentTeamRegionId
            }));

            const noneOption = { level: 1, name: 'None', teamRegionId: -1, parentTeamRegionId: null };
            const allOption = { level: 1, name: 'All', teamRegionId: null, parentTeamRegionId: null };
            const hasNoRegion = !this.user?.teamRegionId || this.user.teamRegionId <= 0;
            const defaultPrefixOptions = (!isTeamManager || hasNoRegion) ? [allOption, noneOption] : [noneOption];

            return defaultPrefixOptions.concat(trs);
        }
    },
    methods: {
        ...mapActions('team', ['FETCH_TEAM_CUSTOMERS', 'FETCH_SKILLS', 'FETCH_TEAM_REGIONS']),

        async getTeamMembers(tripDate = null) {
            this.teamMembers = await this.$store.dispatch('team/FETCH_TEAM_MEMBERS', {
                date: tripDate
            });
        },
        async onGridReady(params) {
            this.gridApi = params.api;

            this.gridApi.addEventListener('columnMoved', this.onColumnMoved);
            this.gridApi.addEventListener('columnVisible', this.onColumnVisibilityChanged);

            params.api.setGridOption('serverSideDatasource', this.createGridServer());

            if (this.isMounted && this.currentFilter) {
                this.defaultColumnState = this.gridApi.getColumnState();
                this.loadFilter(this.currentFilter);
                this.setupColumnToolbox();
            }
        },
        async onGridFilterChanged(ev) {
            if (ev.source && ev.source !== 'api') {
                this.filterModified = true;
            }
        },
        async onGridSortChanged(ev) {
            if (ev.source && ev.source === 'api') {
                return;
            }

            const columnState = this.gridApi.getColumnState();

            this.gridSearchState = {
                ...this.gridSearchState,
                columnState
            };

            this.filterModified = true;
        },
        searchTextChanged() {
            // When the user types, we don't want to re-search on every letter pressed.
            // So we debounce the search to only fire after the user has stopped typing for X milliseconds.
            _.debounce(() => {
                this.gridSearchState = {
                    ...this.gridSearchState,
                    searchText: this.searchText
                };
                this.filterModified = true;
                this.gridApi.refreshServerSide();
            }, 1500)();
        },
        setupCustomFieldDefinitions() {
            // Build a single, deduplicated union of shipment and stop custom field definitions.
            // Requirements:
            // - Include null/empty checks on both collections
            // - If both are empty/missing, final collection is an empty array
            // - Deduplicate by field identity (prefer shipment over stop if same name)

            const isNonEmptyArray = (arr) => Array.isArray(arr) && arr.length > 0;

            const shipmentDefsRaw = this.user && this.user.shipmentCustomFieldDefinitions;
            const stopDefsRaw = this.user && this.user.stopCustomFieldDefinitions;

            const shipmentDefs = isNonEmptyArray(shipmentDefsRaw) ? shipmentDefsRaw : [];
            const stopDefs = isNonEmptyArray(stopDefsRaw) ? stopDefsRaw : [];

            if (shipmentDefs.length === 0 && stopDefs.length === 0) {
                this.customFieldDefinitions = [];
                return;
            }

            // Dedupe by 'name'. Prefer shipment definition on conflicts.
            const byName = Object.create(null);

            // First pass: shipment definitions take precedence
            shipmentDefs.forEach((def) => {
                if (!def || !def.name) {
                    return;
                }
                if (!byName[def.name]) {
                    byName[def.name] = def;
                }
            });

            // Second pass: add stop definitions only if not already present
            stopDefs.forEach((def) => {
                if (!def || !def.name) {
                    return;
                }
                if (!byName[def.name]) {
                    byName[def.name] = def;
                }
            });

            this.customFieldDefinitions = Object.values(byName);
        },

        // Setup columns and custom field columns
        setupColumns(includeServerSideData) {
            const colDefs = [
                // All fields returned are as per the Locate2u.Search.Models.StopElasticModel object
                // Additional IDs are available as follows:
                // StopId, TeamId, UserId,  BrandId, CustomerId, TripId, ShipmentId, TeamRegionId, CarrierTeamId, TeamMemberInvoiceId,  CustomerInvoiceId, RateGroupId,
                {
                    // This column will hold the stop ref, and contain checkboxes for row selection.
                    // It is locked to the left and can't be moved. When scrolling right (due to lots of columns)
                    // this column will always remain visible
                    headerName: 'Reference',
                    field: 'stopRef',
                    headerCheckboxSelection: function headerCheckboxSelection() {
                        return true;
                    },
                    checkboxSelection: function checkboxSelection() {
                        return true;
                    },
                    showDisabledCheckboxes: true,
                    pinned: 'left',
                    lockPosition: 'left',
                    suppressMovable: true,
                    lockPinned: true,
                    valueGetter: (params) => {
                        return {
                            linkPath: `/stops/details/${params.data.stopId}`,
                            linkText: params.data.stopRef,
                            shouldOpenInNewTab: true
                        };
                    },
                    cellRenderer: 'GridLinkRenderer'
                },
                { headerName: 'Source', field: 'source', initialHide: true },
                { headerName: 'Source Ref', field: 'sourceReference', colId: 'sourceReference', initialHide: true },
                { headerName: 'Shipment Ref', field: 'shipmentRef', initialHide: true },
                {
                    headerName: 'Driver Name',
                    field: 'driverName',
                    cellRenderer: 'AssignDriverCellRenderer',
                    cellRendererParams: { teamMembers: this.teamMembers }
                },
                { headerName: 'Driver Email', field: 'driverEmail', initialHide: true },
                { headerName: 'Type', field: 'type' },
                {
                    headerName: 'Status',
                    field: 'status',
                    valueGetter: (params) => {
                        return { status: params.data.status };
                    },
                    cellRenderer: 'StopStatusRenderer',
                    floatingFilterComponent: 'GridDropDownFilter',
                    floatingFilterComponentParams: {
                        dropdownValues: STATUS_CONSTANTS
                    },
                    cellStyle: { textAlign: 'center' }
                },
                {
                    headerName: 'When',
                    field: 'when',
                    filter: 'agDateColumnFilter',
                    valueFormatter: (params) => {
                        if (params.value) {
                            const dt = new Date(params.value);
                            return dt.toDateString();
                        }
                        return '';
                    }
                },
                {
                    headerName: 'Trip Date',
                    field: 'tripDate',
                    filter: 'agDateColumnFilter',
                    valueGetter: (params) => {
                        let text = '';
                        if (params.data.tripDate) {
                            const dt = new Date(params.data.tripDate);
                            text = dt.toDateString();
                        }
                        return {
                            linkPath: `/trips/details/${params.data.tripId}`,
                            linkText: text,
                            shouldOpenInNewTab: true
                        };
                    },
                    cellRenderer: 'GridLinkRenderer',
                    initialHide: true
                },
                {
                    headerName: 'Appointment Date',
                    field: 'appointmentDate',
                    filter: 'agDateColumnFilter',
                    valueFormatter: (params) => {
                        if (params.value) {
                            const dt = new Date(params.value);
                            return dt.toDateString();
                        }
                        return '';
                    },
                    initialHide: true
                },
                {
                    headerName: 'Appointment Time',
                    field: 'appointmentTime',
                    initialHide: true
                },
                {
                    headerName: 'Time Window Start',
                    field: 'timeWindowStart',
                    initialHide: true
                },
                {
                    headerName: 'Time Window End',
                    field: 'timeWindowEnd',
                    initialHide: true
                },
                { headerName: 'Trip Status', field: 'tripStatus', initialHide: true },
                {
                    headerName: 'Item Count',
                    field: 'itemsCount',
                    cellDataType: 'number',
                    filter: 'agNumberColumnFilter',
                    tooltipComponent: 'GridItemDataTooltip',
                    tooltipComponentParams: { headerText: 'Items', tableJsonField: 'itemsJson' },
                    tooltipField: 'itemsCount',
                    initialHide: true
                },
                {
                    headerName: 'Rating',
                    field: 'rating',
                    initialHide: true,
                    cellDataType: 'number',
                    filter: 'agNumberColumnFilter'
                },
                {
                    headerName: 'Duration',
                    field: 'durationMinutes',
                    initialHide: true,
                    cellDataType: 'number',
                    filter: 'agNumberColumnFilter'
                },
                {
                    headerName: 'Attempt',
                    field: 'attempt',
                    initialHide: true,
                    cellDataType: 'number',
                    filter: 'agNumberColumnFilter'
                },
                {
                    headerName: 'Run #',
                    field: 'runNumber',
                    initialHide: true,
                    cellDataType: 'number',
                    filter: 'agNumberColumnFilter'
                },
                { headerName: 'Booked by', field: 'createdBy', initialHide: true }
            ];

            if (includeServerSideData) {
                colDefs.push({
                    headerName: 'Customer Name',
                    field: 'customerName',
                    initialHide: true,
                    floatingFilterComponent: 'GridDropDownFilter',
                    floatingFilterComponentParams: {
                        dropdownValues: this.customerList.map((x) => x.value),
                        includeBlank: true
                    }
                });
            } else {
                colDefs.push({
                    headerName: 'Customer Name',
                    field: 'customerName',
                    initialHide: true,
                    floatingFilterComponent: 'GridDropDownFilter'
                });
            }

            if (includeServerSideData) {
                colDefs.push({
                    headerName: 'Team Region',
                    field: 'teamRegionId',
                    initialHide: true,
                    filter: 'agSetColumnFilter', // This is required for SetFilter
                    floatingFilter: true,
                    floatingFilterComponent: 'TeamRegionDropDownFilter',
                    filterParams: {
                        defaultToNothingSelected: true,
                        values: (params) => {
                            // Provide an array of teamRegionId values
                            params.success(this.teamRegionOptions.map((x) => x.teamRegionId));
                        },
                        valueFormatter: (params) => {
                            // display name in the selected filter
                            const match = this.teamRegionOptions.find((x) => x.teamRegionId === params.value);
                            return match ? match.name : params.value;
                        },
                        cellRenderer: 'TeamRegionFilterCellRenderer',
                        cellRendererParams: {
                            teamRegions: this.teamRegionOptions
                        },
                        refreshValuesOnOpen: true,
                        comparator: (a, b) => 0 // Prevent AG Grid from sorting the values
                    },
                    floatingFilterComponentParams: {
                        dropdownValues: this.teamRegionOptions
                    },
                    cellRenderer: (params) => {
                        const region = this.teamRegionOptions.find((tr) => tr.teamRegionId === params.value);
                        return region ? region.name : params.value; // Show name instead of teamRegionId
                    }
                });
            } else {
                colDefs.push({
                    headerName: 'Team Region',
                    field: 'teamRegionId',
                    initialHide: true,
                    filter: 'agSetColumnFilter', // This is required for SetFilter
                    floatingFilter: true,
                    floatingFilterComponent: 'TeamRegionDropDownFilter',
                    floatingFilterComponentParams: {
                        dropdownValues: this.teamRegionOptions
                    }
                });
            }

            // append the rest of the entries to colDefs
            colDefs.push([
                { headerName: 'Rate Group Name', field: 'rateGroupName', initialHide: true },
                { headerName: 'Contact Name', field: 'contactName' },
                { headerName: 'Contact Phone', field: 'contactPhone', initialHide: true },
                { headerName: 'Contact Email', field: 'contactEmail', initialHide: true },
                { headerName: 'Address', field: 'address' },
                { headerName: 'Location Name', field: 'name', initialHide: true },
                {
                    headerName: 'Driver Invoice',
                    field: 'teamMemberInvoiceNumber',
                    initialHide: true,
                    valueGetter: (params) => {
                        return {
                            linkPath: `/team-member-invoices/details/${params.data.teamMemberInvoiceId}`,
                            linkText: params.data.teamMemberInvoiceNumber,
                            shouldOpenInNewTab: true
                        };
                    },
                    cellRenderer: 'GridLinkRenderer'
                },
                {
                    headerName: 'Customer Invoice',
                    field: 'customerInvoiceNumber',
                    initialHide: true,
                    valueGetter: (params) => {
                        return {
                            linkPath: `/customer-invoices/details/${params.data.customerInvoiceId}`,
                            linkText: params.data.customerInvoiceNumber,
                            shouldOpenInNewTab: true
                        };
                    },
                    cellRenderer: 'GridLinkRenderer'
                }
            ]);

            if (includeServerSideData) {
                colDefs.push({
                    headerName: 'Skills',
                    field: 'skills',
                    initialHide: true,
                    floatingFilterComponent: 'GridDropDownFilter',
                    floatingFilterComponentParams: {
                        dropdownValues: this.skills.map((x) => x.name)
                    },
                    valueGetter: (params) => {
                        // params.data.skills is a json array of strings. We want to convert it to an array of strings.
                        const arr = params.data.skills ? JSON.parse(params.data.skills) : [];

                        return { items: arr };
                    },
                    cellRenderer: 'GridChipsRenderer'
                });
            } else {
                colDefs.push({
                    headerName: 'Skills',
                    field: 'skills',
                    initialHide: true,
                    floatingFilterComponent: 'GridDropDownFilter'
                });
            }

            colDefs.push([
                { headerName: 'Services', field: 'services', initialHide: true },
                { headerName: 'Brand', field: 'brandName', initialHide: true },
                {
                    headerName: 'Custom Fields',
                    field: 'customFields',
                    initialHide: true,
                    filter: null,
                    tooltipComponent: 'GridKeyValueDataTooltip',
                    tooltipComponentParams: { headerText: 'Custom Fields', tableJsonField: 'customFields' },
                    tooltipField: 'customFields',
                    valueGetter: (params) => {
                        if (
                            params.data.customFields &&
                            params.data.customFields !== '' &&
                            params.data.customFields !== '{}'
                        ) {
                            return 'Custom Field List';
                        }
                        return '';
                    }
                },
                { headerName: 'Notes', field: 'notes', initialHide: true },
                { headerName: 'Weight', field: 'weight', initialHide: true },
                { headerName: 'Height', field: 'height', initialHide: true },
                { headerName: 'Width', field: 'width', initialHide: true },
                { headerName: 'Length', field: 'length', initialHide: true },
                { headerName: 'Volume', field: 'volume', initialHide: true },
                { headerName: 'Quantity', field: 'quantity', initialHide: true },
                { headerName: 'Total Cost', field: 'totalCosts', initialHide: true },
                { headerName: 'Total Charge', field: 'totalCharges', initialHide: true }
            ]);

            // Dynamically add custom field definitions as individual columns (hidden by default)
            // Each column uses "customFields.<name>" as its field/colId so server-side filtering/sorting can map it.
            if (this.customFieldDefinitions && this.customFieldDefinitions.length) {
                const dynamicCustomCols = this.customFieldDefinitions.map((def) => this.createCustomFieldColumn(def));
                colDefs.push(dynamicCustomCols);
            }

            this.columnDefs = colDefs.flat();
            return this.columnDefs;
        },
        getCustomFieldValue(params, fieldName) {
            const raw = params?.data?.customFields;
            if (!raw) {
                return null;
            }
            let obj = raw;
            if (typeof raw === 'string') {
                try {
                    obj = JSON.parse(raw);
                } catch (e) {
                    return null;
                }
            }
            if (!obj || typeof obj !== 'object') {
                return null;
            }
            return obj[fieldName];
        },
        createCustomFieldBase(def) {
            const fieldPath = `custom.${def.name}`;
            return {
                headerName: def.label || def.name,
                field: fieldPath,
                colId: fieldPath,
                initialHide: true
            };
        },
        createCustomFieldNumberCol(def) {
            const base = this.createCustomFieldBase(def);
            return {
                ...base,
                cellDataType: 'number',
                filter: 'agNumberColumnFilter',
                valueGetter: (params) => {
                    const v = this.getCustomFieldValue(params, def.name);
                    const n = typeof v === 'number' ? v : parseFloat(v);
                    return Number.isNaN(n) ? null : n;
                }
            };
        },
        createCustomFieldDateCol(def) {
            const base = this.createCustomFieldBase(def);
            return {
                ...base,
                filter: 'agDateColumnFilter',
                valueGetter: (params) => this.getCustomFieldValue(params, def.name),
                valueFormatter: (params) => {
                    const v = params.value;
                    if (!v) {
                        return '';
                    }
                    const dt = new Date(v);
                    return Number.isNaN(dt.getTime()) ? v : dt.toDateString();
                }
            };
        },
        createCustomFieldCheckBoxCol(def) {
            const base = this.createCustomFieldBase(def);
            return {
                ...base,
                cellDataType: 'boolean',
                filter: 'agSetColumnFilter',
                filterParams: { values: [true, false] },
                valueGetter: (params) => {
                    const v = this.getCustomFieldValue(params, def.name);
                    if (typeof v === 'boolean') {
                        return v;
                    }
                    if (typeof v === 'string') {
                        return v.toLowerCase() === 'true' || v === '1' || v === 'yes';
                    }
                    if (typeof v === 'number') {
                        return v === 1;
                    }
                    return null;
                },
                valueFormatter: (params) => {
                    if (params.value === true) {
                        return 'Yes';
                    }
                    if (params.value === false) {
                        return 'No';
                    }
                    return '';
                }
            };
        },
        createCustomFieldTextCol(def) {
            const base = this.createCustomFieldBase(def);
            return {
                ...base,
                filter: 'agTextColumnFilter',
                valueGetter: (params) => {
                    const v = this.getCustomFieldValue(params, def.name);
                    return v == null ? '' : String(v);
                }
            };
        },
        createCustomFieldColumn(def) {
            const type = def.type || 'Text';
            if (type === 'Number') {
                return this.createCustomFieldNumberCol(def);
            }
            if (type === 'Date') {
                return this.createCustomFieldDateCol(def);
            }
            if (type === 'CheckBox') {
                return this.createCustomFieldCheckBoxCol(def);
            }
            return this.createCustomFieldTextCol(def);
        },
        setupColumnToolbox() {
            if (this.gridApi) {
                const columnsToolPanel = this.gridApi.getToolPanelInstance('columns');
                const layout = [
                    // Top-level (ungrouped) columns
                    { colId: 'stopRef' },
                    { colId: 'source' },
                    { colId: 'sourceReference' },
                    { colId: 'shipmentRef' },
                    { colId: 'type' },
                    { colId: 'status' },
                    { colId: 'tripStatus' },
                    { colId: 'attempt' },
                    { colId: 'itemsCount' },
                    { colId: 'rating' },
                    { colId: 'durationMinutes' },
                    { colId: 'runNumber' },
                    { colId: 'createdBy' },
                    { colId: 'customerName' },
                    { colId: 'teamRegionId' },
                    { colId: 'rateGroupName' },
                    { colId: 'address' },
                    { colId: 'name' },
                    { colId: 'skills' },
                    { colId: 'services' },
                    { colId: 'brandName' },
                    { colId: 'customFields' },
                    { colId: 'notes' },

                    // Grouped columns
                    {
                        groupId: 'driverDetails',
                        headerName: 'Driver Details',
                        children: [{ colId: 'driverName' }, { colId: 'driverEmail' }]
                    },
                    {
                        groupId: 'datesTimes',
                        headerName: 'Dates & Times',
                        children: [
                            { colId: 'when' },
                            { colId: 'tripDate' },
                            { colId: 'appointmentDate' },
                            { colId: 'appointmentTime' },
                            { colId: 'timeWindowStart' },
                            { colId: 'timeWindowEnd' }
                        ]
                    },
                    {
                        groupId: 'contactDetails',
                        headerName: 'Contact Details',
                        children: [{ colId: 'contactName' }, { colId: 'contactPhone' }, { colId: 'contactEmail' }]
                    },
                    {
                        groupId: 'invoiceNumbers',
                        headerName: 'Invoice Numbers',
                        children: [{ colId: 'teamMemberInvoiceNumber' }, { colId: 'customerInvoiceNumber' }]
                    },
                    {
                        groupId: 'capacity',
                        headerName: 'Capacity',
                        children: [
                            { colId: 'weight' },
                            { colId: 'height' },
                            { colId: 'width' },
                            { colId: 'length' },
                            { colId: 'volume' },
                            { colId: 'quantity' }
                        ]
                    },
                    {
                        groupId: 'charges',
                        headerName: 'Charges',
                        children: [{ colId: 'totalCosts' }, { colId: 'totalCharges' }]
                    }
                ];

                // Append dynamic custom fields group if present
                if (this.customFieldDefinitions && this.customFieldDefinitions.length) {
                    layout.push({
                        groupId: 'custom',
                        headerName: 'Custom Fields',
                        children: this.customFieldDefinitions.map((def) => ({ colId: `custom.${def.name}` }))
                    });
                }

                columnsToolPanel.setColumnLayout(layout);
            }
        },

        async performSearch(request) {
            this.isGridLoading = true;
            this.gridApi.showLoadingOverlay();

            const api = `/api/jobs/search`;
            const payload = {
                method: 'post',
                data: request
            };
            try {
                const response = await handleRequests(api, payload);
                const stopSearchResult = response.data ? response.data : [];
                this.isGridLoading = false;
                this.gridApi.hideOverlay();
                return stopSearchResult;
            } catch (e) {
                this.isGridLoading = false;
                this.gridApi.hideOverlay();
                return null;
            }
        },
        convertToServerSideRequest(gridRequest) {
            // We update the request to include the search text that sits outside the grid
            const req = {
                ...gridRequest,
                SearchAllFields: this.searchText
            };
            return req;
        },
        createGridServer() {
            // This is a 'server' for the Grid server side model. It acts as a facade for calling our own API.
            // Normally you would not use this if you just want to get all your data in one request and show in the grid.
            // However in this approach we are using ElasticSearch to handle all aspects of paging, filtering, and sorting.
            // So the server side model facade lets us control all of that on the server side.
            const t = this;

            return {
                async getRows(params) {
                    const request = t.convertToServerSideRequest(params.request);

                    const colState = t.gridSearchState ? t.gridSearchState.columnState : this.defaultColumnState;

                    t.gridSearchState = {
                        ...t.gridSearchState,
                        filterModel: request.filterModel,
                        sortModel: request.sortModel,
                        columnState: colState
                    };

                    const result = await t.performSearch(request);

                    if (result) {
                        params.success({
                            rowData: result.stops,
                            rowCount: result.totalStops
                        });
                    } else {
                        params.fail();
                    }
                }

                // Example of a request object that is sent from Ag Grid (params.request)
                // {
                //     "startRow": 0,
                //     "endRow": 100,
                //     "rowGroupCols": [],
                //     "valueCols": [],
                //     "pivotCols": [],
                //     "pivotMode": false,
                //     "groupKeys": [],
                //     "filterModel": {
                //         "tripDate": {
                //             "dateFrom": "2024-01-31 00:00:00",
                //             "dateTo": null,
                //             "filterType": "date",
                //             "type": "equals"
                //         },
                //         "sourceReference": {
                //             "filterType": "text",
                //             "type": "contains",
                //             "filter": "CODE2"
                //         }
                //     },
                //     "sortModel": [
                //         {
                //         "sort": "asc",
                //         "colId": "status"
                //         }
                //     ]
                // }
            };
        },
        onColumnMoved(event) {
            if (event.source === 'api') {
                return;
            }

            const columnState = event.api.getColumnState();

            if (this.gridSearchState) {
                this.gridSearchState = {
                    ...this.gridSearchState,
                    columnState
                };
            } else {
                this.gridSearchState = {
                    columnState
                };
            }

            this.filterModified = true;
        },
        onColumnVisibilityChanged(event) {
            if (event.source === 'api') {
                return;
            }

            const columnState = event.columnApi.getColumnState();

            if (this.gridSearchState) {
                this.gridSearchState = {
                    ...this.gridSearchState,
                    columnState
                };
            } else {
                this.gridSearchState = {
                    columnState
                };
            }

            this.filterModified = true;
        },
        onColumnResized(event) {
            if (event.source === 'api') {
                return;
            }

            if (event.finished) {
                const columnState = event.api.getColumnState();

                if (this.gridSearchState) {
                    this.gridSearchState = {
                        ...this.gridSearchState,
                        columnState
                    };
                } else {
                    this.gridSearchState = {
                        columnState
                    };
                }

                this.filterModified = true;
            }
        },

        // User saved filters

        loadFilter(filter) {
            this.currentFilter = filter;

            if (this.currentFilter && !this.currentFilter.columnState) {
                this.currentFilter.columnState = this.defaultColumnState;
            }

            // Prevent premature changes to grid if its not loaded yet.
            if (this.gridApi && this.isMounted) {
                this.gridApi.setFilterModel(filter.filterModel);
                this.gridApi.applyColumnState({ state: filter.columnState, applyOrder: true });
            }

            this.searchText = filter.searchText || '';
            this.selectedSpecialDate = filter.specialDateFilter || null;
            this.setWhenColumnDateFilter();

            if (this.gridApi && this.isMounted) {
                this.gridApi.refreshServerSide();
            }
        },
        selectedFilterChanged(filterObject) {
            if (filterObject) {
                this.loadFilter(filterObject);
            } else {
                const gridFilter = {
                    filterModel: {},
                    sortModel: [],
                    columnState: this.defaultColumnState
                };
                this.loadFilter(gridFilter);
            }
        },

        // special dates UI methods

        selectSpecialDate(value) {
            this.selectedSpecialDate = value;
            this.specialDateFilterOpen = false;
            this.setWhenColumnDateFilter();
            this.filterModified = true;
        },
        clearSpecialDate() {
            this.selectedSpecialDate = null;
            this.specialDateFilterOpen = false;
            this.setWhenColumnDateFilter();
            this.filterModified = true;
        },
        toggleSpecialDateFilter() {
            this.specialDateFilterOpen = !this.specialDateFilterOpen;
        },
        exitSpecialDatesFilter() {
            this.specialDateFilterOpen = false;
        },

        // common methods for special dates

        setWhenColumnDateFilter() {
            this.gridSearchState = {
                ...this.gridSearchState,
                specialDateFilter: this.selectedSpecialDate
            };

            // selectedSpecialDate: 1 = Today, 2 = Tomorrow, 3 = Yesterday, null = clear
            const today = new Date();
            if (this.selectedSpecialDate === 1) 
                this.setWhenColumnFilter(today);
            else if (this.selectedSpecialDate === 2) {
                const tomorrow = new Date();
                tomorrow.setDate(today.getDate() + 1);
                this.setWhenColumnFilter(tomorrow);
            } else if (this.selectedSpecialDate === 3) {
                const yesterday = new Date();
                yesterday.setDate(today.getDate() - 1);
                this.setWhenColumnFilter(yesterday);
            } else if (this.selectedSpecialDate === null) {
                const existingFilters = this.gridApi.getFilterModel() || {};
                existingFilters.when = null;
                this.gridApi.setFilterModel(existingFilters);
            }
        },
        setWhenColumnFilter(date) {
            const formattedDate = date.toLocaleDateString('en-CA'); // Canadian format is YYYY-MM-DD which the grid needs. This will still use local timezone, not canadian timezone.

            const existingFilters = this.gridApi.getFilterModel() || {};
            existingFilters.when = {
                type: 'equals',
                dateFrom: formattedDate
            };

            this.gridApi.setFilterModel(existingFilters);
        },

        // Bulk actions methods
        onSelectionChanged(event) {
            // Get selected rows from the grid
            const selectedNodes = this.gridApi.getSelectedNodes();
            const selectedData = selectedNodes.map((node) => node.data);

            // Store objects with stopId, shipmentId, and type instead of just stopIds
            this.selectedRows = selectedData.map((item) => ({
                stopId: item.stopId,
                shipmentId: item.shipmentId,
                type: item.type
            }));
        },

        showBulkActionsWizard() {
            this.showWizard = true;
        },
        closeWizard() {
            this.showWizard = false;
        },

        refreshAfterBulkAction() {
            // Refresh the grid data
            this.gridApi.refreshServerSide();

            // Clear selections
            this.selectedRows = [];
            this.gridApi.deselectAll();

            // Close the wizard
            this.closeWizard();
        },
        refreshGrid() {
            // Refresh the grid data
            this.gridApi.refreshServerSide();
        }
    }
};
</script>

<style lang="scss" scoped>
@import '~ag-grid-community/styles/ag-grid.css';
@import '~ag-grid-community/styles/ag-theme-quartz.css';

::v-deep .layout-parent {
    height: 100%;
}

.page-layout {
    padding: 0px;
    height: 86vh;
    width: 100%;
    display: grid;
    grid-template-rows: 70px 1fr;
    row-gap: 10px;
    justify-items: stretch;
}
.page-toolbar {
    width: 100%;
    grid-row: 1;
    display: grid;
    grid-template-columns: 220px 1fr 130px 300px;
    justify-items: stretch;
    align-items: center;
}
.toolbar-bulk-edit {
    grid-column: 1;
    vertical-align: middle;
    margin-right: 20px;
}
.bulk-edit-button {
    width: 100%;
}
.toolbar-text {
    grid-column: 2;
    vertical-align: middle;
}
.toolbar-special {
    grid-column: 3;
    justify-content: left;
    vertical-align: middle;
}
.toolbar-filters {
    grid-column: 4;
    justify-content: right;
    vertical-align: middle;
}
.search-textbox {
    width: 100%;
    border: 1px solid #999;
    border-radius: 8px;
    font-size: 16px;
    padding: 12px 20px 12px 40px;
    background-color: #f8f8f8;
    align-items: center;
    justify-self: center;
}
.page-content {
    grid-row: 2;
    width: 100%;
}
.grid-display {
    width: 100%;
    height: 100%;
    margin-top: 10px;
    margin-bottom: 10px;
}
.ag-theme-quartz-dark {
    --ag-odd-row-background-color: #1f2f3a;
}
.ag-theme-quartz-dark .ag-row-odd {
    background-color: var(--ag-odd-row-background-color);
}
.ag-theme-quartz {
    --ag-odd-row-background-color: #fbfbfb;
}
.ag-theme-quartz .ag-row-odd {
    background-color: var(--ag-odd-row-background-color);
}

.col1 {
    grid-column: 1;
}
.col2 {
    grid-column: 2;
}

.list-item {
    margin: 0px;
    display: flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
}
.when-list-item {
    height: 100%;
    display: grid;
    align-items: left;
    cursor: pointer;
    margin: 0px;
    vertical-align: middle;
    align-items: center;
}

.when-select-item {
    display: grid;
    align-items: left;
}

.when-icon {
    height: 24px;
    width: 24px;
    color: white !important;
}

.when-icon.selected {
    font-weight: bolder;
    color: white !important;
}

.when-text {
    margin-left: 5px;
    vertical-align: middle;
}
.when-icon-option {
    height: 24px;
    width: 24px;
    color: black !important;
}

.date-chip-selector {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0px;
    justify-content: left;
    vertical-align: middle;
}

/* Special Date Filter styles */

.special-date-filter {
    position: absolute;
    background: white;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    z-index: 10;
    top: 70px;
    right: 210px;
    padding: 0px;
    margin: 0px;
    min-width: 220px;
}

.special-date-filter-card {
    padding: 0px;
    margin: 0px;
}

.md-card-header {
    padding: 10px;
    margin: 0px;
    background: #f5f5f5;
    font-size: 13px;
    font-weight: bold;
    color: #333;
}

.chip {
    height: 40px;
    margin: 0px;
    display: flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
}

.fade-scale-enter-active,
.fade-scale-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-scale-enter {
    opacity: 0;
    transform: scale(0.8);
}

.fade-scale-leave-to {
    opacity: 0;
    transform: scale(1.1);
}

/* Bulk Actions Footer */
.bulk-actions-footer {
    position: absolute;
    bottom: 10px;
    left: 10px;
    background-color: #f5f5f5;
    padding: 8px 16px;
    border-radius: 4px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    z-index: 5;
}
</style>
